import { Avatar } from "@mui/material";
import { alpha, useTheme } from "@mui/system";
import { useMemo } from "react";

const UserImage = ({ image, size = 48, name }: { image?: string | null; size?: number; name?: string | null }) => {
    const theme = useTheme();

    // Generate color based on name (if available)
    const avatarColor = useMemo(() => {
        if (!name) return undefined;

        // Simple hash function for the name to generate a consistent color
        const hash = name.split('').reduce((acc, char) => {
            return char.charCodeAt(0) + ((acc << 5) - acc);
        }, 0);

        // Use the hash to pick from a set of nice colors
        const colors = [
            theme.palette.primary.main,
            theme.palette.secondary.main,
            '#00bcd4', // cyan
            '#4caf50', // green
            '#ff9800', // orange
            '#9c27b0', // purple
            '#f44336', // red
            '#3f51b5', // indigo
        ];

        return colors[Math.abs(hash) % colors.length];
    }, [name, theme]);

    // Get initials from name
    const initials = useMemo(() => {
        if (!name) return '?';

        return name
            .split(' ')
            .map(part => part[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    }, [name]);

    if (!image) {
        return (
            <Avatar
                sx={{
                    width: size,
                    height: size,
                    fontSize: size * 0.4,
                    fontWeight: 'bold',
                    bgcolor: avatarColor || theme.palette.primary.main,
                    border: `2px solid ${alpha(theme.palette.background.paper, 0.8)}`,
                    boxShadow: `0 0 0 1px ${alpha(theme.palette.divider, 0.2)}`,
                    color: "#fff",
                }}
            >
                {initials}
            </Avatar>
        );
    }

    return (
        <Avatar
            src={image}
            alt="User Avatar"
            sx={{
                width: size,
                height: size,
                border: `2px solid ${alpha(theme.palette.background.paper, 0.8)}`,
                boxShadow: `0 0 0 1px ${alpha(theme.palette.divider, 0.2)}`,
            }}
        />
    );
};

export default UserImage;
